import type { DynamicTableConfig, Page } from '../../types'
import { CORE_DEPARTMENTS } from '../core/coverSheet'
import { crossGovernmentPathwayDescription, crossGovernmentPathwayTable } from '../pathways/annexB'

const locationHint =
    'What towns and cities have candidates started in this quarter? Enter the number for each location.'

const locationColumns: DynamicTableConfig['columns'] = [
    { key: 'scotland', label: 'Scotland', hint: locationHint },
    {
        key: 'yorkshireAndHumber',
        label: 'Yorkshire and the Humber',
        hint: locationHint
    },
    { key: 'northEast', label: 'North East', hint: locationHint },
    { key: 'northWest', label: 'North West', hint: locationHint },
    { key: 'eastMidlands', label: 'East Midlands', hint: locationHint },
    { key: 'westMidlands', label: 'West Midlands', hint: locationHint },
    { key: 'wales', label: 'Wales', hint: locationHint },
    { key: 'southEast', label: 'South East', hint: locationHint },
    { key: 'southWest', label: 'South West', hint: locationHint },
    { key: 'eastOfEngland', label: 'East of England', hint: locationHint },
    { key: 'london', label: 'London', hint: locationHint },
    { key: 'northernIreland', label: 'Northern Ireland', hint: locationHint }
]

const locationColumnKeys = locationColumns.map(column => column.key)

const columns: DynamicTableConfig['columns'] = [
    {
        key: 'candidatesStarted',
        label: 'How many candidates started employment via Pathway during this quarter',
        hint: "This is the total number of 'new starters' at all grades following recruitment during 1 January to 31 March (Quarter 4)."
    },
    {
        key: 'newStartersAaEo',
        label: 'Total Number of New Starters at AA - EO Grades',
        hint: 'This is the total number of candidates who started a role during the quarter in roles at Grades AA - EO or equivalent.'
    },
    {
        key: 'newStartersHeoPlus',
        label: 'Total Number of New Starters at grade HEO and above',
        hint: 'This is the total number of candidates who started a role during the quarter in roles at Grades HEO and above or equivalent.'
    },
    {
        key: 'awaitingStartDate',
        label: 'How many candidates are currently awaiting a start date?',
        hint: 'Total number of individuals offered a position who accepted, but are going through pre-employment checks, onboarding or awaiting a start date.'
    },
    {
        key: 'rolesProvided',
        label: 'Number of Vacancies/ Roles provided by business/ departments to the Pathway',
        hint: 'This is the total number of roles offered to this GFiE Pathway, including roles that were not filled or were later withdrawn.'
    },
    {
        key: 'permanencyConversions',
        label: 'How many conversions to permanency have occurred for candidates?',
        hint: 'Total number of candidates converted to permanency during this quarter only.'
    },
    {
        key: 'shortestTimeToHire',
        label: 'Shortest Candidate Time to Hire Period (In days)',
        hint: 'This is the shortest candidate period of hire from advert close to provisional offer made, from 1 April 2025 to this quarter end.'
    },
    {
        key: 'longestTimeToHire',
        label: 'Longest Time to Hire Candidate Period (In days)',
        hint: 'This is the longest candidate period of hire from advert close to provisional offer made, from 1 April 2025 to this quarter end.'
    },
    {
        key: 'averageTimeToHire',
        label: 'Average Time to Hire for candidates who accepted provisional offer in Quarter (In days)',
        hint: 'Add the total days from advert close to provisional offer for candidates hired this quarter, then divide by the number of new hires.',
        allowDecimal: true
    },
    ...locationColumns,
    {
        key: 'locationTotal',
        label: 'Total',
        hint: 'Calculated automatically from all location columns.',
        enabled: false,
        calculation: 'rowSum',
        sourceColumns: locationColumnKeys
    },
    {
        key: 'comments',
        label: 'Any comments about data or extra info',
        headerLineLimit: 1,
        inputType: 'text',
        required: false
    }
]

const pathwayRows: DynamicTableConfig['rows'] = [
    { key: 'cpsDiversyfingCareers', label: 'CPS Diversifying CPS Careers' },
    { key: 'dcmsLearnAndGrowPathway', label: 'DCMS Learn & Grow Pathway' },
    { key: 'dfeCareLeavers', label: 'DFE Care Leavers internship' },
    { key: 'dfeSupportedInternships', label: 'DFE Supported Internships' },
    { key: 'workfitDhsc', label: 'DHSC Workfit' },
    { key: 'dwpMovementToWork', label: 'DWP Movement to Work' },
    { key: 'dwpPathwayToSuccess', label: 'DWP - Pathway to Success' },
    {
        key: 'dwpSocialMobilityApprenticeshipProgramme',
        label: 'DWP Social Mobility Apprenticeship'
    },
    { key: 'gfieCareLeavers', label: 'Cabinet Office - Pathway for Care leavers' },
    { key: 'gfieCarers', label: 'Cabinet Office - Pathway for Carers' },
    {
        key: 'gfiePeopleWithConvictions',
        label: 'Cabinet Office Pathway for People with Convictions'
    },
    { key: 'gfieAutisticPeople', label: 'Cabinet Office Pathway for Autistic People' },
    { key: 'gfieVeterans', label: 'Cabinet Office - Pathway for Veterans' },
    {
        key: 'gfiePartnersSpouses',
        label: 'Cabinet Office - Pathway for Military partners/spouses'
    },
    { key: 'homeOfficeMovingForward', label: 'Home Office Moving forward' },
    {
        key: 'homeOfficePathwayToEmployment',
        label: 'Home Office Pathway to Employment'
    },
    { key: 'ipoPathwaysToStem', label: 'IPO - Pathways to STEM Programme' },
    { key: 'civilServiceMasterclass', label: 'MHCLG Civil Service Masterclass' },
    { key: 'mojProbationEmploymentPathway', label: 'MOJ Probation Employment Pathway' },
    {
        key: 'scottishGovernmentFairStart',
        label: 'Scottish Government Employability in Scotland'
    },
    {
        key: 'gcsSteppingForward',
        label: 'Stepping into Communications (GCS Internship Scheme)'
    }
]

export const GFIE_CROSS_GOVERNMENT_ANSWER_FIELDS_BY_PATHWAY = Object.fromEntries(
    pathwayRows.map(row => [row.label, `gfie-pathway-cross-government-${row.key}`])
)

const departmentalOnlyPathways = new Set([
    'dcmsLearnAndGrowPathway',
    'workfitDhsc',
    'dwpSocialMobilityApprenticeshipProgramme',
    'homeOfficeMovingForward',
    'homeOfficePathwayToEmployment',
    'mojProbationEmploymentPathway',
    'scottishGovernmentFairStart'
])

export const GFIE_PATHWAY_OPTIONS = pathwayRows
    .map(row => ({
        option: row.label,
        questions: [
            {
                questionType: 'radio' as const,
                title: 'Is this a cross-government pathway?',
                hint: 'Can other government departments use this pathway?',
                name: GFIE_CROSS_GOVERNMENT_ANSWER_FIELDS_BY_PATHWAY[row.label],
                options: ['Yes', 'No'],
                defaultValue: departmentalOnlyPathways.has(row.key) ? 'No' : 'Yes',
                rules: { required: true }
            }
        ]
    }))
    .sort((left, right) => left.option.localeCompare(right.option, 'en-GB', { sensitivity: 'base' }))

const recruitmentColumnKeys = columns.slice(0, 6).map(column => column.key)
const timeToHireColumnKeys = columns.slice(6, 9).map(column => column.key)

const annexATable: DynamicTableConfig = {
    rowHeader: 'Pathway Name',
    rowHeaderHint: 'To add or remove a pathway, please update form section 1.1 Submitter details',
    columns,
    rows: [
        ...pathwayRows,
        {
            key: 'total',
            label: 'Total',
            enabled: false,
            calculation: 'columnSum'
        }
    ],
    rowFilter: {
        field: 'gfie-pathways',
        match: 'label'
    },
    groups: [
        {
            key: 'recruitment',
            label: 'Recruitment & PEC Data',
            columnKeys: recruitmentColumnKeys
        },
        {
            key: 'timeToHire',
            label: 'Time to Hire Data',
            columnKeys: timeToHireColumnKeys
        },
        {
            key: 'location',
            label: 'Location Data',
            columnKeys: [...locationColumnKeys, 'locationTotal', 'comments']
        }
    ],
    compact: true,
    stickyHeaders: true,
    stickyRowHeader: true,
    highlightActive: true,
    height: 640,
    layout: 'grouped'
}

const gfieCrossGovernmentPathwayTable: DynamicTableConfig = {
    ...crossGovernmentPathwayTable,
    rowHeaderInput: {
        inputType: 'select',
        dropdownOptions: GFIE_PATHWAY_OPTIONS.map(({ option }) => option),
        optionsFrom: {
            selectionField: 'gfie-pathways',
            answerFieldsByOption: GFIE_CROSS_GOVERNMENT_ANSWER_FIELDS_BY_PATHWAY,
            answerValue: 'Yes'
        },
        prepopulateOptions: true
    },
    initialContinuousEditableRowCount: 0,
    sortOnSaveBy: 'rowHeader',
    addRowBackground: 'lightBlue'
}

export const submitterDetails: Page[] = [
    {
        pageType: 'question',
        title: '1.1 Submitter details',
        name: 'submitter-details',
        description:
            'Many thanks for completing this return. By sharing this information you are helping us to get a picture of activity across all accredited Pathways.',
        questions: [
            {
                questionType: 'reportPeriod',
                title: 'Period of Report',
                name: 'report-period',
                defaultValue: 'auto',
                rules: { required: true }
            },
            {
                questionType: 'textInput',
                title: 'Contact email',
                name: 'gfie-contact-email',
                userEmail: true,
                rules: { required: true, email: true }
            },
            {
                questionType: 'select',
                title: 'Department',
                name: 'gfie-department',
                managedOptions: true,
                options: CORE_DEPARTMENTS,
                rules: { required: true }
            },
            {
                questionType: 'checkbox',
                title: 'Please select the pathways you wish to report upon',
                name: 'gfie-pathways',
                managedOptions: true,
                hint: "Please provide data for the Pathway you are accredited to run only to avoid duplicate counting i.e. HMRC using Cabinet Office GFiE Prison Leavers will be counted by the Cabinet Office team and should not feature in HMRC's return.",
                options: GFIE_PATHWAY_OPTIONS,
                rules: { required: true }
            }
        ]
    }
]

export const pathwayDataReturn: Page[] = [
    {
        pageType: 'question',
        title: 'Pathway data return',
        name: 'gfie-pathway-data',
        limitWidth: false,
        questions: [
            {
                questionType: 'dynamicTable',
                title: 'GFiE pathway data',
                name: 'gfie-annex-a-pathway-data-one-table',
                hint: 'Please provide data for the period {report-period.dateRange} ({report-period.quarterNumber})',
                rules: { required: true },
                dynamicTable: annexATable
            }
        ]
    }
]

export const crossGovernmentPathwayReturn: Page[] = [
    {
        pageType: 'question',
        title: '1.3 Cross Government Pathway',
        name: 'gfie-cross-government-pathway-information',
        limitWidth: false,
        description: crossGovernmentPathwayDescription,
        questions: [
            {
                questionType: 'dynamicTable',
                title: 'Cross Government Pathway Information',
                name: 'gfie-cross-government-pathway-information-table',
                hint: 'Please provide data for the period {report-period.dateRange} ({report-period.quarterNumber})',
                rules: { required: true },
                dynamicTable: gfieCrossGovernmentPathwayTable
            }
        ]
    }
]

const annexA: Page[] = [...submitterDetails, ...pathwayDataReturn, ...crossGovernmentPathwayReturn]

export default annexA
